/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * Transfer React Query Hooks — Multi-product + Approval Workflow
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  createTransfer,
  approveTransfer,
  updateAndResubmitTransfer,
  rejectTransfer,
  cancelTransfer,
  getPendingApprovals,
  getTransfers,
  getTransferSummary,
} from '../services/transfer.service';
import { stockKeys } from './useStock';
import type {
  CreateTransferRequest,
  UpdateTransferRequest,
  RejectTransferRequest,
  CancelTransferRequest,
  TransferResult,
  TransferListItem,
  TransferSummary,
  TransferListParams,
  PendingApprovalsParams,
} from '../types/transfer';
import type { PagedResult } from '../types/product';
import type { ApiError } from '@/services/api/types';

export const transferKeys = {
  all: ['venta', 'transfers'] as const,
  list: (params: TransferListParams) => [...transferKeys.all, 'list', params] as const,
  pending: (params: PendingApprovalsParams) => [...transferKeys.all, 'pending', params] as const,
  summary: () => [...transferKeys.all, 'summary'] as const,
};

export function useTransfers(params: TransferListParams) {
  return useQuery<PagedResult<TransferListItem>, ApiError>({
    queryKey: transferKeys.list(params),
    queryFn: () => getTransfers(params),
    staleTime: 15_000,
    placeholderData: (previousData) => previousData,
  });
}

export function usePendingApprovals(params: PendingApprovalsParams) {
  return useQuery<PagedResult<TransferListItem>, ApiError>({
    queryKey: transferKeys.pending(params),
    queryFn: () => getPendingApprovals(params),
    staleTime: 15_000,
    placeholderData: (previousData) => previousData,
  });
}

export function useTransferSummary() {
  return useQuery<TransferSummary, ApiError>({
    queryKey: transferKeys.summary(),
    queryFn: getTransferSummary,
    staleTime: 30_000,
  });
}

export function useCreateTransfer() {
  const queryClient = useQueryClient();

  return useMutation<TransferResult, ApiError, CreateTransferRequest>({
    mutationFn: createTransfer,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: transferKeys.all });
      queryClient.invalidateQueries({ queryKey: stockKeys.all });
    },
  });
}

export function useApproveTransfer() {
  const queryClient = useQueryClient();

  return useMutation<TransferResult, ApiError, string>({
    mutationFn: (id) => approveTransfer(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: transferKeys.all });
      queryClient.invalidateQueries({ queryKey: stockKeys.all });
    },
  });
}

export function useUpdateTransfer() {
  const queryClient = useQueryClient();

  return useMutation<TransferResult, ApiError, { id: string; data: UpdateTransferRequest }>({
    mutationFn: ({ id, data }) => updateAndResubmitTransfer(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: transferKeys.all });
      queryClient.invalidateQueries({ queryKey: stockKeys.all });
    },
  });
}

export function useRejectTransfer() {
  const queryClient = useQueryClient();

  return useMutation<TransferResult, ApiError, { id: string; data: RejectTransferRequest }>({
    mutationFn: ({ id, data }) => rejectTransfer(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: transferKeys.all });
      queryClient.invalidateQueries({ queryKey: stockKeys.all });
    },
  });
}

export function useCancelTransfer() {
  const queryClient = useQueryClient();

  return useMutation<TransferResult, ApiError, { id: string; data?: CancelTransferRequest }>({
    mutationFn: ({ id, data }) => cancelTransfer(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: transferKeys.all });
      queryClient.invalidateQueries({ queryKey: stockKeys.all });
    },
  });
}
